import type { FC } from 'react';

import { Typography } from '@packages/shared';
import { useConfig } from '@packages/utilities';

import type { ResponsiveNavigationItemBaseProps } from './ResponsiveNavigationItemBase';
import { ResponsiveNavigationItemBase } from './ResponsiveNavigationItemBase';

export type ResponsiveNavigationMainItemProps = Omit<
  ResponsiveNavigationItemBaseProps,
  'sxListItem' | 'sxLink'
> & {
  /** If `true`, the category corresponding to the item is the current page */
  isActive?: boolean;
  /** If `true`, the children of the item are displayed in the nav popup */
  isOpen?: boolean;
};

/**
 * Navigation item for the main navigation (always visible)
 */
export const ResponsiveNavigationMainItem: FC<ResponsiveNavigationMainItemProps> = ({
  isActive,
  isOpen,
  children,
  ...baseProps
}) => {
  const {
    header: { hasSmallerFontSize },
  } = useConfig();

  return (
    <ResponsiveNavigationItemBase
      {...baseProps}
      sxListItem={[
        {
          justifyContent: 'center',
          alignItems: 'center',
          flex: { xs: '0 1 50%', lg: 1 },
          position: 'relative',
          cursor: 'pointer',
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'grey.main',
          transition: 'background-color 0.25s borderColor 0.25s',
          '&:hover': {
            backgroundColor: 'grey.light',
          },
        },
        isActive && !isOpen
          ? {
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '0.25rem',
                borderRadius: '0.25rem 0.25rem 0 0',
                backgroundColor: 'primary.main',
              },
            }
          : {},
        isOpen
          ? {
              borderBottomColor: 'transparent',
              backgroundColor: 'grey.light',
            }
          : {},
      ]}
      sxLink={{
        alignSelf: 'stretch',
        justifyContent: 'center',
      }}
    >
      <Typography
        sx={{
          lineHeight: '1.25rem',
          fontSize: {
            xs: hasSmallerFontSize ? '0.93rem' : '1rem',
            xl: hasSmallerFontSize ? '1.06rem' : '1.125rem',
          },
          paddingY: 1.5,
          paddingX: 1,
          whiteSpace: { xs: 'normal', lg: 'nowrap' },
          textAlign: 'center',
        }}
      >
        {children}
      </Typography>
    </ResponsiveNavigationItemBase>
  );
};
